import { useEffect, useState, useCallback } from 'react';
import { createContext } from 'react';
import PropTypes from 'prop-types';

// third-party
import { deserialize } from 'bson';

// project import
import saveFile from 'utils/save-file/saveFile';
import useWebsocket from 'hooks/useWebsocket';
import useUtils from 'hooks/useUtils';

// ==============================|| WEBSOCKET CONTEXT & PROVIDER ||============================== //

const ScriptContext = createContext(null);

function ScriptProvider({ children }) {
  const { sendWsMessage, wsMessage, resetWsMessage, reconnectWs } = useWebsocket();
  const [scriptRunning, setScriptRunning] = useState(false);
  const [scriptRunningId, setScriptRunningId] = useState('');
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState(0);
  const { displaySnackbar } = useUtils();

  const reset = () => setFinished(false);

  const onFinish = useCallback(() => {
    setFinished(true);
    setScriptRunningId('');
    resetWsMessage();
    setProgress(0);
  }, [resetWsMessage]);

  const onBinaryMessage = useCallback(
    (wsMessage) => {
      const bson = deserialize(wsMessage, { promoteBuffers: true });

      if (bson.metadata?.code === 1011) {
        console.error(bson);
        displaySnackbar(bson.metadata.message, 'error');
      } else if (bson.data) {
        saveFile(bson.data, bson.metadata.fileName);
      } else {
        displaySnackbar(bson.metadata.message, 'warning');
      }

      onFinish();
    },
    [displaySnackbar, onFinish]
  );

  const onJsonMessage = useCallback((wsMessage) => {
    const json = JSON.parse(wsMessage);

    switch (json.type) {
      case 'progress':
        setProgress(json.progressValue);
        break;
      case 'running':
        setScriptRunning(json.running);
    }
  }, []);

  const onBeforeUnload = useCallback((e) => {
    e.preventDefault();
    e.returnValue = '';
  }, []);

  useEffect(() => {
    if (scriptRunning) {
      window.addEventListener('beforeunload', onBeforeUnload, { capture: true });
    } else {
      window.removeEventListener('beforeunload', onBeforeUnload, { capture: true });
    }
  }, [scriptRunning, onBeforeUnload]);

  useEffect(() => {
    if (wsMessage instanceof ArrayBuffer) onBinaryMessage(wsMessage);
    else if (wsMessage) onJsonMessage(wsMessage);
  }, [wsMessage, onBinaryMessage, onJsonMessage, resetWsMessage]);

  const runScript = (id) => {
    reset();
    setScriptRunningId(id);
    const payload = { wsType: 'initScript', options: { id } };
    reconnectWs(); // reset connection to increase timeout
    sendWsMessage(JSON.stringify(payload));
  };

  const killScript = () => {
    sendWsMessage(JSON.stringify({ wsType: 'killScript' }));
    onFinish();
  };

  const actions = {
    runScript,
    killScript,
    progress,
    scriptRunning,
    scriptRunningId,
    finished
  };

  return <ScriptContext.Provider value={actions}>{children}</ScriptContext.Provider>;
}

ScriptProvider.propTypes = {
  children: PropTypes.node
};

export { ScriptProvider, ScriptContext };
